import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Chip, Paper, Divider, CircularProgress, Alert, Avatar, Tooltip } from '@mui/material';
import { Download, Delete, Visibility, PictureAsPdf, Image, Description, Refresh, CheckCircle, Cancel } from '@mui/icons-material';
import apiClient from '../config/axios';
import dayjs from 'dayjs';

const ROLES = [
  // { value: 'admin', label: 'Amministratore' },
  { value: 'teacher', label: 'Docente' },
  { value: 'member', label: 'Membro' },
];

const ModalUserEdit = ({ open, onClose, onSubmit, onDeleteUser, mode, form, setForm, user }) => {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  const [userFiles, setUserFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileError, setFileError] = useState('');
  const [deletingFile, setDeletingFile] = useState(null);
  const [filePreviews, setFilePreviews] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRolesChange = (e) => {
    const selectedRoles = e.target.value;
    setForm((prev) => {
      const newForm = { ...prev, roles: selectedRoles };

      // Se il ruolo selezionato non è 'member', imposta il campo level a vuoto (non specificato)
      if (!selectedRoles.includes('member')) {
        newForm.level = '';
      }
      return newForm;
    });
  };

  // Funzioni per la gestione dei file
  const isImageFile = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif'].includes(extension);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <PictureAsPdf color='error' />;
    if (isImageFile(fileName)) return <Image color='primary' />;
    return <Description color='action' />;
  };

  const truncateFileName = (fileName, maxLength = 30) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
    return `${truncatedName}.${extension}`;
  };

  const getFileTypeLabel = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'PDF';
    if (extension === 'jpg' || extension === 'jpeg') return 'JPG';
    if (extension === 'png') return 'PNG';
    return extension?.toUpperCase() || 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const loadUserFiles = async () => {
    if (!user?._id) return;

    setLoadingFiles(true);
    setFileError('');

    try {
      const response = await apiClient.get(`/api/files/user/${user._id}`);
      const files = response.data.data || [];
      setUserFiles(files);

      // Genera anteprime per le immagini
      const newPreviews = {};
      for (const file of files) {
        if (isImageFile(file.fileName)) {
          try {
            const previewResponse = await apiClient.get(`/api/files/view/${encodeURIComponent(file.key)}`, {
              responseType: 'blob',
            });
            const previewUrl = URL.createObjectURL(previewResponse.data);
            newPreviews[file.key] = previewUrl;
          } catch (error) {
            console.error('Error generating preview for:', file.fileName, error);
          }
        }
      }
      setFilePreviews(newPreviews);
    } catch (error) {
      console.error('Error loading user files:', error);
      setFileError('Errore nel caricamento dei file');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDownloadFile = async (fileKey, fileName) => {
    try {
      const response = await apiClient.get(`/api/files/download/${encodeURIComponent(fileKey)}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setFileError('Errore nel download del file');
    }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const response = await apiClient.get(`/api/files/view/${encodeURIComponent(fileKey)}`, {
        responseType: 'blob',
      });

      // Determina il content type basandosi sull'estensione del file
      const extension = fileName.split('.').pop()?.toLowerCase();
      let contentType = 'application/octet-stream';

      if (extension === 'pdf') {
        contentType = 'application/pdf';
      } else if (['jpg', 'jpeg'].includes(extension)) {
        contentType = 'image/jpeg';
      } else if (extension === 'png') {
        contentType = 'image/png';
      } else if (extension === 'gif') {
        contentType = 'image/gif';
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error viewing file:', error);
      setFileError('Errore nella visualizzazione del file');
    }
  };

  const handleDeleteFile = async (fileKey, fileName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il file "${fileName}"?`)) {
      return;
    }

    setDeletingFile(fileKey);

    try {
      await apiClient.delete(`/api/files/${encodeURIComponent(fileKey)}`);
      setUserFiles((prev) => prev.filter((file) => file.key !== fileKey));

      // Rimuovi anche l'anteprima se esiste
      if (filePreviews[fileKey]) {
        URL.revokeObjectURL(filePreviews[fileKey]);
        setFilePreviews((prev) => {
          const newPreviews = { ...prev };
          delete newPreviews[fileKey];
          return newPreviews;
        });
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setFileError("Errore nell'eliminazione del file");
    } finally {
      setDeletingFile(null);
    }
  };

  // Carica i file quando il modal si apre
  useEffect(() => {
    if (open && user?._id) {
      loadUserFiles();
    }

    // Cleanup delle anteprime quando il modal si chiude
    return () => {
      Object.values(filePreviews).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      setFilePreviews({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?._id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth disableEscapeKeyDown disableBackdropClick>
      <DialogTitle>
        {isEdit && 'Modifica Utente'}
        {isView && 'Dettagli Utente'}
      </DialogTitle>
      <DialogContent>
        <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField name='name' label='Nome' value={isView ? user?.name : form.name} onChange={handleChange} required fullWidth InputProps={{ readOnly: isView }} />
          <TextField name='surname' label='Cognome' value={isView ? user?.surname : form.surname} onChange={handleChange} required fullWidth InputProps={{ readOnly: isView }} />
          <FormControl fullWidth>
            <InputLabel>Ruolo</InputLabel>
            <Select
              name='roles'
              label='Ruolo'
              multiple={isView}
              value={isView ? user?.roles || [] : form.roles.length ? form.roles : ['member']}
              onChange={
                isEdit
                  ? (e) => {
                      const selectedRole = e.target.value;
                      setForm((prev) => {
                        const newForm = { ...prev, roles: [selectedRole] };

                        // Se il ruolo selezionato non è 'member', imposta il campo level a vuoto (non specificato)
                        if (selectedRole !== 'member') {
                          newForm.level = '';
                        }

                        return newForm;
                      });
                    }
                  : handleRolesChange
              }
              disabled={isView}
              renderValue={(selected) => (Array.isArray(selected) ? selected.map((role) => ROLES.find((r) => r.value === role)?.label || role).join(', ') : ROLES.find((r) => r.value === selected)?.label || selected)}
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Mostra il campo Anno di Corso sempre, ma abilitalo solo per membri */}
          <FormControl fullWidth>
            <InputLabel>Anno di Corso</InputLabel>
            <Select name='level' label='Anno di Corso' value={isView ? user?.level || '' : form.level} onChange={handleChange} disabled={isView || !(isView ? user?.roles?.includes('member') : form.roles.includes('member'))}>
              <MenuItem value={0}>Da assegnare</MenuItem>
              <MenuItem value={1}>1° anno</MenuItem>
              <MenuItem value={2}>2° anno</MenuItem>
              <MenuItem value={3}>3° anno</MenuItem>
              <MenuItem value={4}>4° anno</MenuItem>
              <MenuItem value={5}>5° anno</MenuItem>
              <MenuItem value={6}>Oltre il 5° anno</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sezione Consensi */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Consensi
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user?.flagStatuto ? <CheckCircle color='success' /> : <Cancel color='error' />}
              <Typography variant='body2'>Statuto dell'associazione: {user?.flagStatuto ? 'Accettato' : 'Non accettato'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user?.flagPrivacy ? <CheckCircle color='success' /> : <Cancel color='error' />}
              <Typography variant='body2'>Privacy Policy: {user?.flagPrivacy ? 'Accettata' : 'Non accettata'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user?.flagPicture ? <CheckCircle color='success' /> : <Cancel color='error' />}
              <Typography variant='body2'>Liberatoria foto: {user?.flagPicture ? 'Accettata' : 'Non accettata'}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Sezione File Associati */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant='h6'>File Associati</Typography>
            <IconButton onClick={loadUserFiles} disabled={loadingFiles} size='small'>
              <Refresh />
            </IconButton>
          </Box>

          {fileError && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setFileError('')}>
              {fileError}
            </Alert>
          )}

          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : userFiles.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Nessun file associato a questo utente
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <List dense>
                {userFiles.map((file) => (
                  <ListItem key={file.key} sx={{ px: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{isImageFile(file.fileName) && filePreviews[file.key] ? <Avatar src={filePreviews[file.key]} variant='rounded' sx={{ width: 40, height: 40, mr: 1 }} /> : getFileIcon(file.fileName)}</Box>
                    <ListItemText
                      primary={
                        <Tooltip title={file.fileName} placement='top'>
                          <span>{truncateFileName(file.fileName)}</span>
                        </Tooltip>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={getFileTypeLabel(file.fileName)} size='small' variant='outlined' />
                          <Typography variant='caption' color='text.secondary'>
                            {formatFileSize(file.size)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {new Date(file.lastModified).toLocaleDateString('it-IT')}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton size='small' color='primary' onClick={() => handleViewFile(file.key, file.fileName)} title='Visualizza'>
                        <Visibility />
                      </IconButton>
                      <IconButton size='small' color='primary' onClick={() => handleDownloadFile(file.key, file.fileName)} title='Scarica'>
                        <Download />
                      </IconButton>
                      {isEdit && (
                        <IconButton size='small' color='error' onClick={() => handleDeleteFile(file.key, file.fileName)} disabled={deletingFile === file.key} title='Elimina'>
                          {deletingFile === file.key ? <CircularProgress size={18} /> : <Delete />}
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {isEdit && (
            <Button onClick={() => onDeleteUser?.(user)} color='error' variant='contained'>
              Rimuovi account
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose}>Annulla</Button>
          {isEdit && (
            <Button onClick={onSubmit} variant='contained'>
              Salva
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModalUserEdit;
